import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, AlertTriangle, ExternalLink, FileText } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";
import { PageLoader } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AgreementExtraFilesSection } from "@/features/contractors/components/AgreementExtraFilesSection";
import { acceptanceDeadlineDaysLabel } from "@/features/contractors/components/AcceptanceDeadlineDaysField";

// ── Types ──────────────────────────────────────────────────────────────────────

interface LineItemType {
  id: string;
  name: string;
  unit: string;
  base_rate: string;
  high_priority_rate: string | null;
  attachment_required: boolean;
  attachment_count_per_unit: boolean;
  // Kontrola zdjęć w plikach pozycji realizacyjnej — wykonawca widzi to PRZED wyjazdem.
  allow_screenshots: boolean;
  geotag_required: boolean;
}

interface PenaltyTerm {
  id: string;
  line_item_type: string | null;
  line_item_type_name: string | null;
  penalty_per_day_pct: string | null;
  penalty_per_day_amount: string | null;
  flat_penalty_amount: string | null;
  max_penalty_pct: string | null;
  max_penalty_amount: string | null;
  termination_after_days: number | null;
  termination_allowed: boolean;
  is_delay_penalty: boolean;
  notes: string;
}

interface PenaltyRecord {
  id: string;
  order: string | null;
  order_number: string | null;
  penalty_term_description: string | null;
  delay_days: number;
  amount: string;
  is_termination: boolean;
  note: string;
  applied_by_name: string | null;
  is_forgiven: boolean;
  forgiven_note: string;
  is_settled: boolean;
  settlement_note: string;
  settled_at: string | null;
  created_at: string;
}

interface Agreement {
  id: string;
  contractor: string;
  contractor_name: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  document_managed_file: string | null;
  document_managed_file_detail: { id: string; original_filename: string; content_type: string; size_bytes: number; url: string | null } | null;
  pricing_managed_file: string | null;
  pricing_managed_file_detail: (ManagedFileRef & { id: string; original_filename: string }) | null;
  // Kwota minimalna zamówienia ustalona w umowie — null = bez minimum.
  min_order_amount?: string | null;
  // Termin na przyjęcie zamówienia (dni od wysłania); null = domyślne 5 dni.
  acceptance_deadline_days?: number | null;
  auto_accept?: boolean;
  line_item_types: LineItemType[];
  penalty_terms: PenaltyTerm[];
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function ContractorAgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: agreement, isLoading } = useQuery({
    queryKey: ["contractor-agreement", id],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement }>(`/agreements/${id}/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });
  usePageTitle(agreement && `Umowa: ${agreement.title}`);

  const { data: penaltyRecords = [] } = useQuery({
    queryKey: ["contractor-agreement-penalty-records", id],
    queryFn: () =>
      apiClient
        .get<{ data: PenaltyRecord[] }>(`/agreements/${id}/penalty-records/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

  if (isLoading) return <PageLoader />;
  if (!agreement) return null;

  const isExpired = agreement.valid_until && new Date(agreement.valid_until) < new Date();

  return (
    <div className="max-w-4xl">
      {/* Back link */}
      <Link
        to="/contractor/agreements"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-5"
      >
        <ArrowLeft size={15} />
        Wróć do listy umów
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{agreement.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{agreement.contractor_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isExpired ? (
              <Badge color="red">Wygasła</Badge>
            ) : (
              <Badge color="green">Aktywna</Badge>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Data podpisania</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {new Date(agreement.signed_date).toLocaleDateString("pl")}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Ważna do</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {agreement.valid_until
                ? new Date(agreement.valid_until).toLocaleDateString("pl")
                : "Bezterminowo"}
            </p>
          </div>
          {!agreement.auto_accept && (
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Termin na przyjęcie zamówienia</p>
              <p className="font-medium text-gray-900 dark:text-gray-100" title="Po tym terminie nieprzyjęte zamówienie może zostać anulowane przez zamawiającego">
                {acceptanceDeadlineDaysLabel(agreement.acceptance_deadline_days)} od wysłania
              </p>
            </div>
          )}
          {agreement.min_order_amount != null && (
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Kwota minimalna zamówienia</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {parseFloat(agreement.min_order_amount).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
              </p>
            </div>
          )}
          {agreement.document_managed_file_detail?.url && (
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Dokument</p>
              <a
                href={agreement.document_managed_file_detail.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline font-medium"
              >
                <FileText size={14} />
                Pobierz PDF
                <ExternalLink size={12} />
              </a>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Cennik</p>
            <ManagedFileLink
              file={agreement.pricing_managed_file_detail}
              icon={<FileText size={14} />}
              iconSize={14}
              empty={
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  Brak cennika
                </span>
              }
              className="font-medium"
            >
              Pobierz cennik
            </ManagedFileLink>
          </div>
        </div>
      </div>

      {/* Inne pliki umowy — aneksy i załączniki. Strona umowy czyta swoje pliki
          (ta sama bramka co skan umowy); oznaczaniem i wgrywaniem zajmuje się GK. */}
      <AgreementExtraFilesSection agreementId={agreement.id} canManage={false} />

      {/* Line items */}
      <section className="mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Pozycje realizacyjne ({agreement.line_item_types.length})
        </h2>
        {agreement.line_item_types.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak zdefiniowanych pozycji realizacyjnych.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs sm:min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa pozycji</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jednostka</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka bazowa</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka priorytetowa</th>
                  <th className="text-center px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Załącznik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {agreement.line_item_types.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{item.name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{item.unit}</td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                      {formatAmount(item.base_rate)} PLN
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                      {item.high_priority_rate ? `${formatAmount(item.high_priority_rate)} PLN` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.attachment_required ? (
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span className="text-xs font-medium text-[var(--color-primary)] dark:text-[var(--color-primary)]">Wymagany</span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {item.attachment_count_per_unit ? "ilość = ilość pozycji" : "1 na pozycję"}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Niewymagany</span>
                      )}
                      {(item.geotag_required || item.allow_screenshots) && (
                        <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-gray-400">
                          {[
                            item.geotag_required ? "wymagany geotag na zdjęciach" : null,
                            item.allow_screenshots ? "zrzuty ekranu dozwolone" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Penalty terms */}
      <section className="mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-500" />
          Kary umowne — warunki ({agreement.penalty_terms.length})
        </h2>
        {agreement.penalty_terms.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak zdefiniowanych kar umownych.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {agreement.penalty_terms.map((term) => (
              <div
                key={term.id}
                className="bg-white dark:bg-gray-800 p-4"
              >
                <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {term.line_item_type_name
                    ? `Pozycja: ${term.line_item_type_name}`
                    : "Kara ogólna (całe zamówienie)"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {term.is_delay_penalty ? (
                    <>
                      {term.penalty_per_day_pct && (
                        <span>
                          Stawka/dzień: <strong className="text-gray-800 dark:text-gray-200">{term.penalty_per_day_pct}%</strong> wartości pozycji
                        </span>
                      )}
                      {term.penalty_per_day_amount && (
                        <span>
                          Stawka/dzień: <strong className="text-gray-800 dark:text-gray-200">{formatAmount(term.penalty_per_day_amount)} PLN</strong>
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="col-span-2">
                      Kara jednorazowa: <strong className="text-gray-800 dark:text-gray-200">{formatAmount(term.flat_penalty_amount ?? "0")} PLN</strong>
                    </span>
                  )}
                  {term.max_penalty_pct && (
                    <span>
                      Maks. kara: <strong className="text-gray-800 dark:text-gray-200">{term.max_penalty_pct}%</strong> wartości
                    </span>
                  )}
                  {term.max_penalty_amount && (
                    <span>
                      Maks. kara: <strong className="text-gray-800 dark:text-gray-200">{formatAmount(term.max_penalty_amount)} PLN</strong>
                    </span>
                  )}
                  {term.termination_after_days && (
                    <span>
                      Zerwanie po: <strong className="text-gray-800 dark:text-gray-200">{term.termination_after_days} dniach</strong> opóźnienia
                    </span>
                  )}
                </div>
                {term.termination_allowed && (
                  <p className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle size={12} /> Możliwość zerwania z winy podwykonawcy
                  </p>
                )}
                {term.notes && (
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">{term.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Applied penalty records */}
      <section className="mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" />
          Naliczone kary ({penaltyRecords.length})
        </h2>
        {penaltyRecords.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak naliczonych kar umownych.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {penaltyRecords.map((record) => (
              <div
                key={record.id}
                className="bg-white dark:bg-gray-800 p-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatAmount(record.amount)} PLN
                      </span>
                      {record.is_termination && (
                        <Badge color="red">Zerwanie umowy</Badge>
                      )}
                      {record.is_forgiven && (
                        <Badge color="green">Umorzona</Badge>
                      )}
                      {record.is_settled && !record.is_forgiven && (
                        <Badge color="blue">Rozliczona</Badge>
                      )}
                      {!record.is_forgiven && !record.is_settled && (
                        <Badge color="yellow">Oczekująca</Badge>
                      )}
                    </div>

                    {record.order_number && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Zamówienie: <span className="font-medium">{record.order_number}</span>
                      </p>
                    )}
                    {record.delay_days > 0 && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Opóźnienie: <span className="font-medium">{record.delay_days} dni</span>
                      </p>
                    )}
                    {record.penalty_term_description && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-0.5">{record.penalty_term_description}</p>
                    )}
                    {record.note && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{record.note}</p>
                    )}
                    {record.is_forgiven && record.forgiven_note && (
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                        Umorzono: {record.forgiven_note}
                      </p>
                    )}
                    {record.is_settled && record.settlement_note && (
                      <p className="text-sm text-[var(--color-primary)] dark:text-blue-400 mt-1">
                        Rozliczono: {record.settlement_note}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                    {new Date(record.created_at).toLocaleDateString("pl")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
